import React from 'react';
import axios from 'axios';
import { Box, Chip, CircularProgress, Stack, Typography, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import { AGRO_STATUS_CONFIG as STATUS_CONFIG } from './agroStatus';
import { finiteNumber, formatNumber, latestSeasonSamples } from './taskMapData';
const PARAM_LABELS = {
    ph: 'pH',
    humus: 'Гумус',
    nitrogen: 'N — азот',
    phosphorus: 'P — фосфор',
    potassium: 'K — калий',
};
const PARAM_UNITS = {
    ph: '',
    humus: '%',
    nitrogen: 'мг/кг',
    phosphorus: 'мг/кг',
    potassium: 'мг/кг',
};
const PARAM_TO_ROW_FIELD = {
    ph: 'agro_ph',
    humus: 'agro_humus',
    nitrogen: 'agro_nitrogen',
    phosphorus: 'agro_phosphorus',
    potassium: 'agro_potassium',
};
export default function FertilityMapView({ token, field, season, onMapUrlChange, onCollapse, onClose }) {
    const [parameter, setParameter] = React.useState('nitrogen');
    const [summary, setSummary] = React.useState(null);
    const [summaryLoading, setSummaryLoading] = React.useState(true);
    const [zones, setZones] = React.useState(null);
    const [zonesLoading, setZonesLoading] = React.useState(true);
    const [zonesNotFound, setZonesNotFound] = React.useState(false);
    const [points, setPoints] = React.useState([]);
    const [pointsLoading, setPointsLoading] = React.useState(true);
    // Сводка по всем показателям — тот же эндпоинт, что уже использует
    // AgrochemBlock на основном дашборде, один запрос на весь список.
    React.useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setSummaryLoading(true);
        setSummary(null);
        axios.get(`/agrochemical/dashboard/${field.id}/${season}`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then((response) => { if (active)
            setSummary(response.data); })
            .catch(() => { if (active)
            setSummary(null); })
            .finally(() => { if (active)
            setSummaryLoading(false); });
        return () => { active = false; controller.abort(); };
    }, [field.id, season, token]);
    React.useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setPointsLoading(true);
        setPoints([]);
        axios.get(`/agrochemical/list/${field.id}`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then((response) => {
            var _a;
            if (active)
                setPoints(latestSeasonSamples((_a = response.data) !== null && _a !== void 0 ? _a : [], season));
        })
            .catch(() => { if (active)
            setPoints([]); })
            .finally(() => { if (active)
            setPointsLoading(false); });
        return () => { active = false; controller.abort(); };
    }, [field.id, season, token]);
    // Пространственные зоны для выбранного параметра — отдельный запрос,
    // только под текущий переключённый слой карты.
    React.useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setZonesLoading(true);
        setZonesNotFound(false);
        setZones(null);
        onMapUrlChange(null);
        const url = `/agrochemical/fertility/${field.id}/${season}/${parameter}`;
        axios.get(url, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then((response) => {
            var _a;
            if (!active)
                return;
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.meta) {
                setZones(response.data.meta);
                onMapUrlChange(url);
            }
            else {
                setZones(null);
                setZonesNotFound(true);
                onMapUrlChange(null);
            }
        })
            .catch(() => {
            if (!active)
                return;
            setZones(null);
            setZonesNotFound(true);
            onMapUrlChange(null);
        })
            .finally(() => { if (active)
            setZonesLoading(false); });
        return () => { active = false; controller.abort(); };
    }, [parameter, field.id, season, token, onMapUrlChange]);
    const paramKeys = Object.keys(PARAM_LABELS);
    return (React.createElement(Box, { sx: { p: 1.5, minWidth: 0 } },
        React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", sx: { mb: 1 } },
            React.createElement(Typography, { variant: "subtitle2", fontWeight: 700 }, "\u041A\u0430\u0440\u0442\u0430 \u043F\u043B\u043E\u0434\u043E\u0440\u043E\u0434\u0438\u044F \u043F\u043E\u0447\u0432\u044B"),
            React.createElement(Stack, { direction: "row", spacing: 0.25 },
                React.createElement(Tooltip, { title: "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043D\u0438\u0436\u043D\u044E\u044E \u043F\u0430\u043D\u0435\u043B\u044C" },
                    React.createElement(IconButton, { size: "small", onClick: onCollapse, "aria-label": "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043D\u0438\u0436\u043D\u044E\u044E \u043F\u0430\u043D\u0435\u043B\u044C" },
                        React.createElement(ExpandMoreIcon, { fontSize: "small" }))),
                React.createElement(Tooltip, { title: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043C\u043E\u0434\u0443\u043B\u044C" },
                    React.createElement(IconButton, { size: "small", onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043C\u043E\u0434\u0443\u043B\u044C" },
                        React.createElement(CloseIcon, { fontSize: "small" }))))),
        summaryLoading && (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 1 } },
            React.createElement(CircularProgress, { size: 16 }),
            React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0430\u0433\u0440\u043E\u0445\u0438\u043C\u0438\u0438\u2026"))),
        !summaryLoading && (!summary || !summary.date) && (React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mb: 1 } }, "\u0410\u043D\u0430\u043B\u0438\u0437 \u043F\u043E\u0447\u0432\u044B \u0437\u0430 \u044D\u0442\u043E\u0442 \u0441\u0435\u0437\u043E\u043D \u043D\u0435 \u0432\u043D\u0435\u0441\u0451\u043D \u2014 \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435 \u00AB\u0412\u043D\u0435\u0441\u0435\u043D\u0438\u0435 \u0434\u0430\u043D\u043D\u044B\u0445\u00BB")),
        !summaryLoading && summary && summary.date && (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mb: 1 } },
                "\u0410\u043D\u0430\u043B\u0438\u0437 \u043E\u0442 ",
                new Date(summary.date).toLocaleDateString('ru-RU'),
                ", ",
                summary.sample_count,
                " \u043F\u0440\u043E\u0431",
                summary.sample_count === 1 ? 'а' : summary.sample_count < 5 ? 'ы' : ''),
            React.createElement(Stack, { direction: "row", spacing: 2 },
                React.createElement(Stack, { spacing: 0.5, sx: { minWidth: 200 } }, paramKeys.map((key) => {
                    const value = summary.values[key];
                    const status = summary.statuses[key];
                    const cfg = status ? STATUS_CONFIG[status] : null;
                    const isSelected = parameter === key;
                    return (React.createElement(Stack, { key: key, direction: "row", justifyContent: "space-between", alignItems: "center", onClick: () => setParameter(key), sx: {
                            cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1,
                            backgroundColor: isSelected ? '#f0f0f0' : 'transparent',
                            border: isSelected ? '1px solid #ccc' : '1px solid transparent',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        } },
                        React.createElement(Typography, { variant: "body2", fontWeight: isSelected ? 700 : 400 }, PARAM_LABELS[key]),
                        React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                            (React.createElement(Typography, { variant: "body2", fontWeight: 600 },
                                formatNumber(value, key === 'ph' || key === 'humus' ? 1 : 0),
                                finiteNumber(value) && PARAM_UNITS[key] ? ` ${PARAM_UNITS[key]}` : '')),
                            finiteNumber(value) && cfg && (React.createElement(Chip, { label: cfg.label, size: "small", sx: { color: cfg.color, backgroundColor: cfg.bg, fontWeight: 600, height: 20 } })))));
                })),
                React.createElement(Stack, { spacing: 0.5, justifyContent: "center", sx: { minWidth: 140 } }, ['norm', 'medium', 'low'].map((status) => {
                    const cfg = STATUS_CONFIG[status];
                    return (React.createElement(Stack, { key: status, direction: "row", spacing: 1, alignItems: "center" },
                        React.createElement(Box, { sx: { width: 12, height: 12, borderRadius: '50%', backgroundColor: cfg.color } }),
                        React.createElement(Typography, { variant: "caption" }, cfg.label === 'норма' ? 'Норма' : cfg.label === 'средн.' ? 'Средний' : 'Низкий')));
                }))),
            summary.recommendations.length > 0 && (React.createElement(Box, { sx: { mt: 1.5, p: 1.5, backgroundColor: '#f5f5f0', borderRadius: 1 } }, summary.recommendations.map((text, i) => (React.createElement(Typography, { key: i, variant: "caption", display: "block", color: "text.secondary" }, text))))),
            React.createElement(Box, { sx: { mt: 1.5 } },
                React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mb: 0.5 } },
                    "\u0420\u0430\u0441\u043F\u0440\u0435\u0434\u0435\u043B\u0435\u043D\u0438\u0435 \u043F\u043E \u043F\u043B\u043E\u0449\u0430\u0434\u0438 \u043F\u043E\u043B\u044F \u2014 ",
                    PARAM_LABELS[parameter]),
                zonesLoading && (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                    React.createElement(CircularProgress, { size: 14 }),
                    React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u0421\u0442\u0440\u043E\u0438\u043C \u043A\u0430\u0440\u0442\u0443\u2026"))),
                !zonesLoading && zonesNotFound && (React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u0414\u043B\u044F \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0433\u043E \u043F\u043E\u043A\u0430\u0437\u0430\u0442\u0435\u043B\u044F \u043D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445 \u0441 \u043A\u043E\u043E\u0440\u0434\u0438\u043D\u0430\u0442\u0430\u043C\u0438 \u043B\u0438\u0431\u043E \u043A\u0430\u0440\u0442\u0443 \u043D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C.")),
                !zonesLoading && zones && (React.createElement(Stack, { direction: "row", spacing: 1 }, ['low', 'medium', 'norm'].map((status) => {
                    const percent = zones.percents[status];
                    if (!finiteNumber(percent))
                        return null;
                    const cfg = STATUS_CONFIG[status];
                    return (React.createElement(Chip, { key: status, label: `${cfg.label}: ${percent.toFixed(1)}%`, size: "small", sx: { color: cfg.color, backgroundColor: cfg.bg, fontWeight: 600 } }));
                })))),
            React.createElement(Box, { sx: { mt: 1.5 } },
                React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mb: 0.5 } }, "\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u043F\u043E \u043A\u0430\u0436\u0434\u043E\u0439 \u0442\u043E\u0447\u043A\u0435"),
                pointsLoading && (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                    React.createElement(CircularProgress, { size: 14 }),
                    React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0442\u043E\u0447\u0435\u043A\u2026"))),
                !pointsLoading && points.length === 0 && (React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u041D\u0435\u0442 \u043E\u0442\u0434\u0435\u043B\u044C\u043D\u044B\u0445 \u043F\u0440\u043E\u0431 \u0437\u0430 \u044D\u0442\u0443 \u0434\u0430\u0442\u0443")),
                !pointsLoading && points.length > 0 && (React.createElement(Table, { size: "small" },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: { py: 0.5, fontSize: 12 } }, "\u0414\u0430\u0442\u0430"),
                            paramKeys.map((key) => (React.createElement(TableCell, { key: key, align: "right", sx: {
                                    py: 0.5, fontSize: 12,
                                    fontWeight: parameter === key ? 700 : 400,
                                    backgroundColor: parameter === key ? '#f0f0f0' : 'transparent'
                                } },
                                PARAM_LABELS[key],
                                PARAM_UNITS[key] ? `, ${PARAM_UNITS[key]}` : ''))))),
                    React.createElement(TableBody, null, points.map((row) => (React.createElement(TableRow, { key: row.id },
                        React.createElement(TableCell, { sx: { py: 0.5, fontSize: 12 } }, new Date(row.agro_year).toLocaleDateString('ru-RU')),
                        paramKeys.map((key) => (React.createElement(TableCell, { key: key, align: "right", sx: {
                                py: 0.5, fontSize: 12,
                                backgroundColor: parameter === key ? '#f8f8f8' : 'transparent'
                            } }, formatNumber(row[PARAM_TO_ROW_FIELD[key]], key === 'ph' || key === 'humus' ? 1 : 0)))))))))))))));
}
